import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { ExternalLinkIconGlyph } from '@shapeable/icons';
import { useLink } from '@shapeable/ui';

const cls = classNames('truncated-external-link');

// Temporary UX trial: truncated URL chip with hover icon + faint inset shadow.

export type TruncatedExternalLinkProps = Classable & {
  href: string;
  label?: string;
  maxWidth?: string;
  title?: string;
};

export const TruncatedExternalLinkDefaultProps: Omit<TruncatedExternalLinkProps, 'href'> = {
  maxWidth: '16rem',
};

type ContainerProps = {
  $maxWidth: string;
};

const ContainerStyles = breakpoints({
  base: css`
    display: inline-flex;
    align-items: center;
    max-width: ${({ $maxWidth }: ContainerProps) => $maxWidth};
    vertical-align: bottom;
    padding: ${theme.UNIT(0.5)} ${theme.UNIT(1.5)};
    border-radius: ${theme.UNIT(1)};
    text-decoration: none;
    color: ${theme.COLOR('subtle')};
    ${theme.TRANSITION('color', 'box-shadow')};

    &:hover {
      color: ${theme.COLOR('primary')};
      box-shadow: inset 0 0 10px 2px #0000000C;

      .${cls.sub('icon')} {
        width: 14px;
        opacity: 1;
        margin-left: ${theme.UNIT(1.5)};
      }
    }
  `,
});

const LabelStyles = breakpoints({
  base: css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    text-decoration: underline;
    text-underline-offset: 0.12em;
  `,
});

const IconWrapStyles = breakpoints({
  base: css`
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    width: 0;
    opacity: 0;
    overflow: hidden;
    margin-left: 0;
    ${theme.TRANSITION('width', 'opacity', 'margin-left')};
  `,
});

const My = {
  Container: styled.span.attrs(cls.attr('container'))<ContainerProps>`${ContainerStyles}`,
  Label: styled.span.attrs(cls.attr('label'))`${LabelStyles}`,
  IconWrap: styled.span.attrs(cls.attr('icon'))`${IconWrapStyles}`,
};

export const TruncatedExternalLink: Shapeable.FC<TruncatedExternalLinkProps> = (props) => {
  const { className, href, label, maxWidth, title } = {
    ...TruncatedExternalLinkDefaultProps,
    ...props,
  };

  const { Link } = useLink();
  const displayLabel = label || href;

  return (
    <Link href={href} target="_blank" rel="noopener noreferrer" title={title || displayLabel}>
      <My.Container className={cls.name(className)} $maxWidth={maxWidth}>
        <My.Label>{displayLabel}</My.Label>
        <My.IconWrap aria-hidden>
          <ExternalLinkIconGlyph colors={{ fill: 'primary' }} size={14} />
        </My.IconWrap>
      </My.Container>
    </Link>
  );
};

TruncatedExternalLink.defaultProps = TruncatedExternalLinkDefaultProps;
TruncatedExternalLink.cls = cls;
